/* eslint-disable lingui/no-unlocalized-strings -- Storage keys, not UI copy. */

/*
 * The visitor's analytics choice (First Round Privacy Policy 7.5). Analytics is on unless they turn
 * it off in Cookie settings. The choice lives in this browser; firstround.com.au and theresume.io
 * keep separate storage, so the choice also rides on the `ft` link between them (attribution.ts).
 */

export type AnalyticsConsent = "granted" | "denied";

const STORAGE_KEY = "analytics_consent";

type Listener = (consent: AnalyticsConsent) => void;
const listeners = new Set<Listener>();
let memoryChoice: AnalyticsConsent | null = null;

/** The explicit choice, or null when the visitor has never made one (analytics on). */
export const getConsentChoice = (): AnalyticsConsent | null => {
  if (memoryChoice) return memoryChoice;
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "granted" || value === "denied" ? value : null;
  } catch {
    return null;
  }
};

export const analyticsAllowed = () => getConsentChoice() !== "denied";

/** Saves the choice and tells the analytics tools (see mixpanel.ts applyConsent). */
export const setConsentChoice = (consent: AnalyticsConsent) => {
  memoryChoice = consent;
  try {
    localStorage.setItem(STORAGE_KEY, consent);
  } catch {
    // Storage blocked: the choice still applies to this page.
  }
  for (const listener of listeners) listener(consent);
};

export const onConsentChange = (listener: Listener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

// The Cookie settings dialog: one instance in Providers, opened from footers and menus.
type OpenListener = (open: boolean) => void;
const openListeners = new Set<OpenListener>();

export const openCookieSettings = () => {
  for (const listener of openListeners) listener(true);
};

export const onCookieSettingsOpen = (listener: OpenListener) => {
  openListeners.add(listener);
  return () => {
    openListeners.delete(listener);
  };
};
